import 'react-activity/dist/react-activity.css'; // react-activityのインジケータ用
import 'react-responsive-modal/styles.css';
import '../styles/global.css';
import '../styles/nprogress.css';
import '../styles/react-responsive-modal-custom-style.css'; // react-responsive-modal/styles.css のスタイルを上書きのため後に書く

import * as Sentry from '@sentry/browser';
import App, { AppContext, AppProps } from 'next/app';
import Router from 'next/router';
import { DefaultSeo } from 'next-seo';
import NProgress from 'nprogress';
import React, { FC, ReactNode, useEffect } from 'react';
import SnackbarProvider from '~/shared/components/shared/SnackbarProvider';
import { GetConfigUtil, isBrowser } from '~/shared/utils/envUtil';
import { LoggingCategory } from '~/utils/constants/loggingCategory';
import { logger } from '~/utils/loggerUtil';
import { sendDataLayerEvent } from '~/utils/measurementUtils';
import { getGAParamsFromLocalStorage, LocalStorage, LocalStorageKeys } from '~/utils/storageUtils';
const ENV_CONFIG = GetConfigUtil.getPublicStaticConfig();
const IS_DEV = ENV_CONFIG.IS_DEV;
if (!IS_DEV) {
  Sentry.init({
    dsn: ENV_CONFIG.SENTRY_DSN,
    environment: ENV_CONFIG.ENVIRONMENT
  });
}

/**
 * NProgress
 */
NProgress.configure({
  showSpinner: false
});
Router.events.on('routeChangeStart', url => {
  logger.debug(`Loading: ${url}`);
  NProgress.start();
});
Router.events.on('routeChangeComplete', () => NProgress.done());
Router.events.on('routeChangeError', () => NProgress.done());
Router.events.on('routeChangeComplete', () => window.scrollTo(0, 0));
class MyApp extends App {
  constructor(props: AppProps) {
    super(props);
    if (isBrowser && !LocalStorage.isSameDomainReferrer) {
      LocalStorage.setLandingPage();
      LocalStorage.setReferrer();
      LocalStorage.setGAParams();
      sendDataLayerEvent(LoggingCategory.HOTELS_SET_GA_PARAMS, {
        gaParams: JSON.stringify(getGAParamsFromLocalStorage())
      });
    }
  }
  componentDidMount() {
    // WebFontLoaderが内部でwindowを参照するため、Dynamic Importする
    if (typeof window !== 'undefined') {
      if (sessionStorage.getItem('fonts')) {
        // If the font had been loaded before
        // Just active the font immediately
        document.documentElement.classList.add('wf-active');
      }

      // We use dynamic import to prevent "window is not defined" error
      import('webfontloader').then(WebFontLoader => {
        WebFontLoader.load({
          timeout: 3000,
          google: {
            families: ['Noto+Sans+JP:400,700']
          },
          custom: {
            families: ['Noto+Sans+JP:400,700'],
            urls: ['https://fonts.googleapis.com/css?family=Noto+Sans+JP:400,700&display=swap&subset=japanese']
          },
          fontactive: () => {
            sessionStorage.setItem('fonts', 'true');
          }
        });
      });
    }
  }
  public render() {
    const {
      Component,
      pageProps
    } = this.props;
    return <>
        <DefaultSeo titleTemplate={'%s｜こころから'} openGraph={{
        type: 'website',
        locale: 'ja_JP',
        site_name: 'こころから'
      }} twitter={{
        site: '@cocolocala_jp',
        cardType: 'summary_large_image'
      }} facebook={{
        appId: '301081897050480'
      }} />
        <LocalStorageEventHandleProvider>
          <SnackbarProvider>
            <Component {...pageProps} />
          </SnackbarProvider>
        </LocalStorageEventHandleProvider>
      </>;
  }
  componentDidCatch(error, errorInfo) {
    if (!IS_DEV) {
      Sentry.withScope(scope => {
        Object.keys(errorInfo).forEach(key => {
          scope.setExtra(key, errorInfo[key]);
        });
        Sentry.captureException(error);
      });
    }
  }
}
MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);
  return {
    ...appProps
  };
};
export default MyApp;

// LocalStorageの変更を通知する
const LocalStorageEventHandleProvider: FC<{
  children: ReactNode;
}> = props => {
  const changeLocalStorageKeyListener = (e: StorageEvent) => {
    // LocalStorageのutmパラメータのキーが変更されたときにDataLayerのイベントを送る
    if (e.key === LocalStorageKeys.utmSource || e.key === LocalStorageKeys.utmMedium || e.key === LocalStorageKeys.utmCampaign) {
      sendDataLayerEvent(LoggingCategory.HOTELS_CHANGE_GA_PARAMS, {
        gaParams: JSON.stringify(getGAParamsFromLocalStorage())
      });
    }
  };
  useEffect(() => {
    if (isBrowser) {
      window.addEventListener('storage', changeLocalStorageKeyListener);
    }
    return () => {
      if (isBrowser) {
        // アンマウント時にListener削除
        window.removeEventListener('storage', changeLocalStorageKeyListener);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <>{props.children}</>;
};